import axios from 'axios'

import axiosInstance from 'api/axiosInstance'
import { type StoryMessage } from 'types/api/message'
import { type IMessage } from 'types/interfaces/IMessage'

export type ConversationsParams = {
  page: number
  search?: string | null
  sort?: string | null
}

export const getConversations = async (
  params: ConversationsParams,
  signal?: AbortSignal
): Promise<any> => {
  const { data } = await axiosInstance.get(
    '/api/chat/conversations',
    {
      params,
      signal
    }
  )

  return data
}

export type ConversationMessagesCursor = {
  created_at: string
  id: string
}

export type GetConversationMessagesParams = {
  // Legacy support
  page?: number

  // Cursor pagination
  limit?: number
  beforeCreatedAt?: string
  beforeId?: string

  search?: string | null
}

export const getConversationMessages = async (
  id: string | null,
  params: GetConversationMessagesParams = {}
) => {
  if (!id) {
    return undefined
  }

  const { data } = await axiosInstance({
    method: 'GET',
    url: `/api/chat/conversation/${id}/messages`,
    params
  })

  return data
}

export const setPollShowChat = async (
    pollId: string,
    showChat: boolean
) => {
    const { data } = await axiosInstance({
        method: 'PATCH',
        url: `api/chat/poll/${pollId}/show-chat`,
        data: {
            show_chat: showChat
        }
    })

    return data
}

export const createConversation = async (userId: string) => {
    const messageData = {
        users: [userId]
    }
    const { data } = await axiosInstance({
        method: 'POST',
        url: 'api/chat/conversation',
        data: messageData
    })
    return data
}

export const sendConversationMessage = async (
  message: IMessage,
  conversationId: string | null,
  type: string = 'text'
) => {
  const messageData: any = {
    ...message,
    type,
  }

  const url = `api/chat/message/conversation/${conversationId}`

  const { data } = await axiosInstance({
    method: 'POST',
    url,
    data: messageData,
  })

  return data
}

export const deleteConversationMessage = async (message_id: string) => {
    const { data } = await axiosInstance({
        method: 'DELETE',
        url: `api/chat/message/${message_id}`
    })
    return data
}


export const getMassMessages = async () => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: '/api/chat/mass-messages'
    })

    return data?.data
}

export const createMassMessageForGroup = async (
  message: IMessage,
  groupId: string | null,
  previousMessagesId?: string | string[] | null,
  type: string = 'text',
  opts?: {
    userIds?: string[] | null
  }
) => {
  const normalizedGroupId =
    typeof groupId === 'string' &&
    groupId.trim() !== ''
      ? groupId
      : null

  const normalizedUserIds =
    normalizedGroupId === null &&
    Array.isArray(opts?.userIds)
      ? Array.from(
          new Set(
            opts?.userIds.filter(
              id =>
                typeof id === 'string' &&
                id.trim() !== ''
            )
          )
        )
      : []

  const messageData: any = {
    ...message,
    message_id: previousMessagesId ?? null,
    type
  }

  if (
    normalizedGroupId === null &&
    normalizedUserIds.length > 0
  ) {
    messageData.user_ids = normalizedUserIds
  }

  const url =
    normalizedGroupId !== null
      ? `/api/chat/message/group/${normalizedGroupId}`
      : '/api/chat/message/group'

  const { data } = await axiosInstance({
    method: 'POST',
    url,
    data: messageData
  })

  return data
}

/* export const createMassMessageForUsers = async (
    message: IMessage,
    massmessageId: string,
    recipients?: {
        recipient?: number[] | number | null
    }
) => {
    const messageData = {
        ...message,
        recipients: recipients?.recipient,
        mass_message_id: massMessageId
    }

    const { data } = await axiosInstance({
        method: 'POST',
        url: '/api/chat/messages/mass-message',
        data: messageData
    })
    return data
}
 */
export const deleteMassMessage = async (id: number | string) => {
    const { data } = await axiosInstance({
        method: 'DELETE',
        url: `/api/chat/mass-message/${id}`
    })

    return data
}

export const sendStoryMessage = async (
    payload: StoryMessage,
    storyId: string
) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: `api/chat/message/story/${storyId}`,
        data: payload
    })
    return data
}

export const deleteConversation = async (id: string | null) => {
    if (id) {
        const { data } = await axiosInstance({
            method: 'DELETE',
            url: `api/chat/conversation/${id}`
        })
        return data
    }
}

export const getQuickContacts = async () => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: '/api/chat/quick-contacts'
    })

    return data
}

export const typing = async (conversationId: string) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: `/api/chat/message/conversation/${conversationId}/typing`
    })
    return data
}

export const notTyping = async (conversationId: string) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: `/api/chat/message/conversation/${conversationId}/notyping`
    })
    return data
}

export const muteConversation = async (params: {
    new_message: 0 | 1
    other_user_id: number
}) => {
    const { data } = await axiosInstance({
        method: 'PUT',
        url: '/api/user/notification-setting-types',
        data: {
            new_message: params.new_message,
            other_user_id: params.other_user_id,
            type: 'in_app'
        }
    })

    return data
}

export const getGifById = async (id: number | string) => {
    const { data } = await axios({
        method: 'GET',
        url: `https://api.giphy.com/v1/gifs/${id}?api_key=${process.env.REACT_APP_GIPHY_KEY}`
    })

    return data
}

export const purchaseMessage = async (
    messageId: string,
    method?: string,
    card_id?: string,
    taxRate?:any,
    taxRateFee?:any
) => {
    const { data } = await axiosInstance({
        method: 'POST',
        data: { message_id: messageId, payment_method: method, card_id , tax:taxRate , tax_rate:taxRateFee },
        url: `/api/chat/message/${messageId}/buy`
    })

    return data
}

/* export const getPolls = async () => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: '/api/post/poll/ask-poll'
    })

    return data?.data
}
 */
export const reactToMessage = async (type: string, messageId: string) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: `api/chat/message/${messageId}/add_reaction`,
        data: {
            type
        }
    })
    return data
}
